// src/components/NotesManagement.js
import React, { useEffect, useState } from "react";
import "./NotesAddSection.css";

const baseUrl = "https://apiportal.mbsays.com/public/api";
const token = sessionStorage.getItem("authToken");
const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
const currentUserId = storedUser?.id;
const isAdmin = storedUser?.role === "admin" || storedUser?.is_admin === 1;

const headers = {
  "Content-Type": "application/json",
  Accept: "application/json",
  ...(token && { Authorization: `Bearer ${token}` }),
};

const NotesManagement = () => {
  /* ----------------- State ----------------- */
  const [notes, setNotes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentNote, setCurrentNote] = useState(null);

  /* ----------------- API çağrıları ----------------- */
  useEffect(() => {
    (async () => {
      await Promise.all([fetchNotes(), fetchBrands()]);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchNotes = async () => {
    try {
      const res = await fetch(`${baseUrl}/notes`, { headers });
      const data = await res.json();
      setNotes(data);
    } catch (e) {
      console.error("Notlar alınamadı:", e);
    }
  };

  const fetchBrands = async () => {
    try {
      const res = await fetch(`${baseUrl}/brands`, { headers });
      const data = await res.json();
      setBrands(data);
    } catch (e) {
      console.error("Markalar alınamadı:", e);
    }
  };

  /* ----------------- Form aç/kapa ----------------- */
  const openAddForm = () => {
    setCurrentNote({ brand_id: "", title: "", note: "" });
    setIsEditing(false);
    setModalOpen(true);
  };
  const openEditForm = (note) => {
    setCurrentNote({ ...note });
    setIsEditing(true);
    setModalOpen(true);
  };
  const closeForm = () => {
    setModalOpen(false);
    setCurrentNote(null);
  };

  /* ----------------- Input değişimleri ----------------- */
  const handleChange = ({ target: { name, value } }) =>
    setCurrentNote((p) => ({
      ...p,
      [name]:
        name === "brand_id" ? (value === "" ? "" : parseInt(value, 10)) : value,
    }));

  /* ----------------- Kaydet / Güncelle ----------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !Number.isInteger(currentNote.brand_id) ||
      !currentNote.title.trim() ||
      !currentNote.note.trim()
    ) {
      alert("Marka, başlık ve not alanları zorunlu.");
      return;
    }

    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `${baseUrl}/notes/${currentNote.id}`
      : `${baseUrl}/notes`;

    try {
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(currentNote),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || res.statusText);
      }
      await fetchNotes();
      closeForm();
    } catch (e) {
      console.error("Not kaydedilemedi:", e);
      alert(e.message || "Bilinmeyen hata");
    }
  };

  /* ----------------- Sil ----------------- */
  const deleteNote = async (id) => {
    if (!window.confirm("Bu notu silmek istediğinize emin misiniz?")) return;

    try {
      const res = await fetch(`${baseUrl}/notes/${id}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw new Error("Silinemedi");
      await fetchNotes();
    } catch (e) {
      console.error("Silme hatası:", e);
      alert(e.message || "Bilinmeyen hata");
    }
  };

  /* ----------------- Yardımcı ----------------- */
  const fmt = (d) => (d ? new Date(d).toLocaleString("tr-TR") : "-");
  const shorten = (str, len = 60) =>
    str.length > len ? `${str.slice(0, len - 3)}…` : str;

  /* ----------------- Render ----------------- */
  return (
    <div className="notes-management">
      <h2>Not Yönetimi</h2>

      {!modalOpen && (
        <button className="create-note-button" onClick={openAddForm}>
          Yeni Not Ekle
        </button>
      )}

      {/* ------------- Modal Form ------------- */}
      {modalOpen && (
        <div className="add-note-form">
          <h3>{isEditing ? "Notu Düzenle" : "Yeni Not Ekle"}</h3>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Marka</label>
              <select
                name="brand_id"
                value={currentNote.brand_id}
                onChange={handleChange}
                required
              >
                <option value="">Seçiniz</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Başlık</label>
              <input
                type="text"
                name="title"
                value={currentNote.title}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Not</label>
              <textarea
                name="note"
                value={currentNote.note}
                onChange={handleChange}
                rows={6}
                required
              />
            </div>

            <div className="form-buttons">
              <button type="submit" className="submit-button">
                {isEditing ? "Güncelle" : "Kaydet"}
              </button>
              <button type="button" className="cancel-button" onClick={closeForm}>
                İptal
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ------------- Liste ------------- */}
      {loading ? (
        <p className="loading-text">Yükleniyor…</p>
      ) : (
        <div className="notes-table-container">
          <h3>Eklenmiş Notlar</h3>
          <table className="notes-table">
            <thead>
              <tr>
                <th>Marka</th>
                <th>Başlık</th>
                <th>Not</th>
                <th>Oluşturan</th>
                <th>Oluşturulma</th>
                <th>Güncelleme</th>
                <th>Aksiyon</th>
              </tr>
            </thead>
            <tbody>
              {notes.map((n) => {
                const canEdit = n.user_id === currentUserId || isAdmin;
                const canDelete = isAdmin;
                return (
                  <tr key={n.id} onClick={() => canEdit && openEditForm(n)} style={{ cursor: canEdit ? 'pointer' : 'default' }}>
                    <td>{n.brand?.name}</td>
                    <td>{n.title}</td>
                    <td>{shorten(n.note)}</td>
                    <td>{n.user?.name}</td>
                    <td>{fmt(n.created_at)}</td>
                    <td>{fmt(n.updated_at)}</td>
                    <td className="actions" onClick={(e) => e.stopPropagation()}>
                      {canEdit && (
                        <button className="edit-btn" onClick={() => openEditForm(n)}>
                          Düzenle
                        </button>
                      )}
                      {canDelete && (
                        <button className="delete-btn" onClick={() => deleteNote(n.id)}>
                          Sil
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>

          </table>
        </div>
      )}
    </div>
  );
};

export default NotesManagement;
